/**
 * Copyright © 2025 Farmlands Co-operative Society Limited. All rights reserved.
 *
 * This document contains confidential information and is the property of Farmlands.
 * Unauthorised reproduction or distribution of any part of this publication is prohibited without written consent.
 */

import dayjs from "dayjs";

import { DURATION_OPTIONS_SHORT } from "../constants";

/**
 * Formats a date string to the specified format
 * @param dateString - The date string to format (can be null or undefined)
 * @param format - The format string to use (defaults to 'DD/MM/YYYY')
 * @returns Formatted date string, 'Not specified' if dateString is null/undefined, or 'Invalid date' if date is invalid
 */
export const formatDate = (
  dateString: string | null | undefined,
  format = "DD/MM/YYYY"
): string => {
  if (!dateString) {
    return "Not specified";
  }
  const date = dayjs(dateString);
  return date.isValid() ? date.format(format) : "Invalid date";
};

export const shortDurationLabelByValue = new Map(
  DURATION_OPTIONS_SHORT.map((o) => [o.value, o.label])
);

/**
 * Formats an activity's schedule date and duration for display, e.g. "25/12/2025, 01:00 PM (30 minutes)"
 * @param scheduleDate - The schedule date string to format (can be null or undefined)
 * @param duration - The duration value to look up a label for (can be null or undefined)
 * @returns Formatted "date (duration)" string, or "Unknown" if scheduleDate is missing/invalid
 */
export const formatScheduleDateAndDuration = (
  scheduleDate: string | null | undefined,
  duration?: number | null
): string => {
  const date = dayjs(scheduleDate);
  if (!date.isValid()) {
    return "Unknown";
  }

  const durationString =
    duration && shortDurationLabelByValue.has(duration)
      ? ` (${shortDurationLabelByValue.get(duration)})`
      : "";

  return `${date.format("DD/MM/YYYY, hh:mm A")}${durationString}`;
};
